import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import { ProjectApi } from "../../api/authority/generated/index.ts";
import UserProjectItem from "./UserProjectItem.jsx";
import { GlobalContext } from "../../utils/context/GlobalContextProvider.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import Button from "../../components/Button.jsx";
import Modal from "../../components/Modal.jsx";
import FormContainer from "../../components/form/FormContainer.jsx";
import FormControl from "../../components/form/FormControl.jsx";
import FormError from "../../components/form/FormError.jsx";
import toast from "react-hot-toast";
import { useErrorTranslation } from "../../api/utilities/translateErrorMessage.js";
import {Panel, SortableHeader, TablePagination, TableSearch} from "../admin/ReportingLab.jsx";

const fetchUserProjects = async (authorityConfiguration, userId, {page, pageSize, searchTerm, sort}) => {
    const query = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        sortBy: sort.key,
        sortDirection: sort.direction,
    });
    if (searchTerm !== '') query.set('searchTerm', searchTerm);

    const response = await fetch(
        `${authorityConfiguration.basePath}/api/v1/user/${encodeURIComponent(userId)}/projects?${query.toString()}`,
        {
            headers: authorityConfiguration.headers,
        }
    );

    if (!response.ok) {
        throw response;
    }

    return response.json();
};

export function nextUserProjectsSort(currentSort, key) {
    return {
        key,
        direction: currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc',
    };
}

export const DEFAULT_PROJECT_LOGO_URL = import.meta.VITE_AUTHORITY_URL + '/static/images/no-logo.png';

export default function UserProjectList() {
    const { t } = useTranslation();
    const { et } = useErrorTranslation();
    const { context, setCurrentProjectId, getAuthorityConfiguration, resetCurrentUserProjects } = useContext(GlobalContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const page = parseInt(searchParams.get('page') ?? '1');
    const [isDownloading, setIsDownloading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [pageSize, setPageSize] = useState(25);
    const [sort, setSort] = useState({key: 'lastInteraction', direction: 'desc'});
    const [addProjectModalOpened, setAddProjectModalOpened] = useState(false);
    const [addProjectLoading, setAddProjectLoading] = useState(false);
    const [projectInput, setProjectInput] = useState({
        name: '',
        logoUrl: DEFAULT_PROJECT_LOGO_URL,
    });
    const [projectCreateError, setProjectCreateError] = useState(null);
    const authorityConfiguration = useMemo(
        () => getAuthorityConfiguration(),
        [getAuthorityConfiguration],
    );
    const navigate = useNavigate();

    useEffect(() => {
        const normalizedSearchTerm = searchValue.trim();
        if (normalizedSearchTerm === searchTerm) return undefined;

        const timeout = window.setTimeout(() => {
            setSearchTerm(normalizedSearchTerm);
            setSearchParams({page: 1});
        }, 300);

        return () => window.clearTimeout(timeout);
    }, [searchTerm, searchValue, setSearchParams]);

    useEffect(() => {
        let active = true;
        if (!context.currentIdentity?.user.id) {
            setResult({data: [], pagination: {page: 1, itemsPerPage: pageSize, itemCount: 0}});
            setIsDownloading(false);
            return undefined;
        }

        setIsDownloading(true);
        setError(null);
        fetchUserProjects(authorityConfiguration, context.currentIdentity.user.id, {
            page,
            pageSize,
            searchTerm,
            sort,
        })
            .then((res) => {
                if (active) setResult(res);
            })
            .catch((e) => {
                if (active) setError(e);
            })
            .finally(() => {
                if (active) setIsDownloading(false);
            });

        return () => { active = false; };
    }, [authorityConfiguration, context.currentIdentity?.user.id, page, pageSize, searchTerm, sort]);

    // Handle project creation
    useEffect(() => {
        if (!addProjectLoading) return;

        new ProjectApi(authorityConfiguration)
            .createProject({
                projectInput: projectInput,
            })
            .then((res) => {
                toast.success(t('project.created'));
                resetCurrentUserProjects();
                setCurrentProjectId(res.id);
                setAddProjectModalOpened(false);
                setProjectInput({ name: '', logoUrl: DEFAULT_PROJECT_LOGO_URL });
                navigate(`/project/${res.id}/settings`);
            })
            .catch((e) => {
                et(e).then((errorMsg) => {
                    setProjectCreateError(errorMsg);
                });
            })
            .finally(() => {
                setAddProjectLoading(false);
            });
    }, [addProjectLoading, authorityConfiguration, projectInput, t, et, resetCurrentUserProjects, setCurrentProjectId, navigate]);

    const actionButton = useMemo(
        () => (
            <Button className={'gap-2'} onClick={() => setAddProjectModalOpened(true)}>
                <FontAwesomeIcon icon={fas.faPlus} /> {t('project.create')}
            </Button>
        ),
        [t]
    );

    return (
        <WideSecureLayout title={t('projects.projects')} actions={actionButton}>
            <Panel title={null} loading={isDownloading}>
                <TableSearch value={searchValue} onChange={setSearchValue} visible t={t}/>
                {error && <FormError error={error}/>}
                {result?.data && <>
                    <div className="overflow-x-auto">
                        <table>
                            <thead><tr>
                                <SortableHeader label={t('project.name')} sortKey="name" sort={sort} onSort={(key) => { setSort((current) => nextUserProjectsSort(current, key)); setSearchParams({page: 1}); }}/>
                                <th>{t('projects.plan')}</th>
                                <SortableHeader label={t('project.last-activity-in-project')} sortKey="lastInteraction" sort={sort} onSort={(key) => { setSort((current) => nextUserProjectsSort(current, key)); setSearchParams({page: 1}); }} align="right"/>
                                <th><span className="sr-only">{t('actions')}</span></th>
                            </tr></thead>
                            <tbody>
                            {result.data.length > 0 && result.data.map((project) => (
                                <UserProjectItem
                                    key={project.id}
                                    project={project}
                                    onProjectIdSelected={setCurrentProjectId}
                                />
                            ))}
                            {result.data.length === 0 && (
                                <tr>
                                    <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={4}>
                                        {t('no-results')}
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                    <TablePagination
                        page={result.pagination?.page ?? page}
                        pageSize={result.pagination?.itemsPerPage ?? pageSize}
                        totalItems={result.pagination?.itemCount ?? result.data.length}
                        onPageChange={(nextPage) => setSearchParams({page: nextPage})}
                        onPageSizeChange={(nextPageSize) => { setPageSize(nextPageSize); setSearchParams({page: 1}); }}
                        t={t}
                    />
                </>}
            </Panel>

            {/* Add project modal */}
            <Modal
                className={'w-full lg:w-11/12 max-w-lg'}
                visible={addProjectModalOpened}
                title={t('project.create')}
                setVisible={setAddProjectModalOpened}
                loading={addProjectLoading}
            >
                <FormContainer>
                    {projectCreateError && <FormError error={projectCreateError} />}
                    <FormControl
                        label={t('project.name')}
                        type={'projectName'}
                        variant="modern"
                        value={projectInput.name}
                        onChange={(e) =>
                            setProjectInput({
                                ...projectInput,
                                name: e.target.value,
                            })
                        }
                    />
                    <div className="flex items-center justify-end mt-6">
                        <Button onClick={() => setAddProjectLoading(true)}>
                            {t('project.create')}
                        </Button>
                    </div>
                </FormContainer>
            </Modal>
        </WideSecureLayout>
    );
}
